import { z } from 'zod';
import { isIsoDay } from '../utils/checklist.js';
import { moneyAmountSchema } from './expense.js';
const isoDaySchema = z.string().refine(isIsoDay, 'Not a valid date (YYYY-MM-DD)');
/**
 * A deposit: exactly one of a percent of the total or a fixed amount, due on
 * signing or on a day. `dueDate` is written by accept, never by a caller that
 * could then contradict `due`.
 */
export const depositTermsSchema = z
    .object({
    percent: z.number().finite().gt(0).max(100).optional(),
    amount: moneyAmountSchema.refine((v) => v > 0, 'A deposit must be more than 0').optional(),
    due: z.union([z.literal('signing'), isoDaySchema]),
})
    .strict()
    .refine((t) => (t.percent === undefined) !== (t.amount === undefined), {
    message: 'Give the deposit as a percent or an amount, not both',
});
export const quoteLineInputSchema = z.object({
    /** The item it prices; null or absent is an extra. */
    planningItemId: z.string().uuid().nullable().optional(),
    description: z.string().trim().min(1).max(500),
    quantity: z.number().finite().gt(0).max(1_000_000),
    unitPrice: moneyAmountSchema,
});
const quoteFiguresShape = {
    totalAmount: moneyAmountSchema.nullable().optional(),
    depositTerms: depositTermsSchema.nullable().optional(),
    validUntil: isoDaySchema.nullable().optional(),
    notes: z.string().max(4000).nullable().optional(),
    /** Replaces every line; absent leaves them as they are. */
    lineItems: z.array(quoteLineInputSchema).max(500).optional(),
    /** Uploaded documents to file on the quote. */
    documentIds: z.array(z.string().uuid()).max(20).optional(),
};
/** `POST /expenses/:id/quotes` — a quote that arrived without being asked for in the app. */
export const createQuoteSchema = z.object({
    vendorId: z.string().uuid(),
    ...quoteFiguresShape,
});
/** `PATCH /quotes/:id` — typed or AI-read figures. */
export const recordQuoteSchema = z
    .object(quoteFiguresShape)
    .refine((v) => Object.values(v).some((x) => x !== undefined), 'Nothing to record');
/** `POST /expenses/:id/quotes/request` */
export const requestQuotesSchema = z.object({
    vendorIds: z.array(z.string().uuid()).min(1, 'Choose at least one vendor').max(25),
    subject: z.string().trim().min(1).max(300),
    bodyHtml: z.string().max(50_000),
    /** Put the need's items in the letter as a table. */
    includeItems: z.boolean().default(true),
    ccSelf: z.boolean().optional(),
});
/** `POST /quotes/:id/accept` */
export const acceptQuoteSchema = z.object({
    /** Tell the vendors whose open quotes this declines. Off unless asked. */
    notifyOthers: z.boolean().default(false),
});
export const templateNeedSchema = z.object({
    key: z.string().trim().min(1).max(64),
    name: z.string().trim().min(1).max(255),
    vendorCategoryId: z.string().uuid().nullable(),
    budgetAmount: moneyAmountSchema.nullable().optional(),
    sortOrder: z.number().int().min(0),
});
/** A template list's needs: keys unique, so each item points at exactly one. */
export const templateNeedsSchema = z
    .array(templateNeedSchema)
    .max(200)
    .refine((needs) => new Set(needs.map((n) => n.key)).size === needs.length, {
    message: 'Template need keys must be unique',
});
/** `POST /expenses/:id/book` — book a need directly, with nothing to shop for. */
export const bookNeedSchema = z
    .object({
    vendorId: z.string().uuid().nullable().optional(),
    vendorName: z.string().trim().max(255).nullable().optional(),
    price: moneyAmountSchema,
    dueDate: isoDaySchema.nullable().optional(),
})
    .refine((v) => !!v.vendorId || !!v.vendorName, {
    message: 'Choose the vendor this is booked with',
});
//# sourceMappingURL=quote.js.map